import React, { useState } from "react";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Typography,
    TablePagination,
    TextField,
    InputAdornment,
    IconButton,
    Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { LandingFooter } from "./LandingFooter";
import NavBar from "components/navbar";
import { useLazyGetDDLRoutesShipmentsQuery } from "store/services/publicApi";

const RouteRoutineList = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [fetchRoutes, { data: routesData, isLoading }] =
        useLazyGetDDLRoutesShipmentsQuery();

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput]);

    React.useEffect(() => {
        fetchRoutes({
            active: "true",
            page: page + 1,
            limit: rowsPerPage,
            search: debouncedSearch
        });
    }, [fetchRoutes, page, rowsPerPage, debouncedSearch]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setDebouncedSearch("");
    };

    // Helper function to convert route name to URL-friendly slug
    const createSlug = (routeName) => {
        return routeName
            .toLowerCase()
            .replace(/\s+/g, '-')           // Replace spaces with hyphens
            .replace(/[^\w\-]+/g, '')       // Remove non-word chars except hyphens
            .replace(/\-\-+/g, '-')         // Replace multiple hyphens with single hyphen
            .replace(/^-+/, '')             // Trim hyphens from start
            .replace(/-+$/, '');            // Trim hyphens from end
    };

    const handleViewSchedule = (route) => {
        const slug = createSlug(route.routeName);
        // Navigate with clean URL and pass route data via state
        navigate(`/routine/${slug}`, {
            state: {
                routeId: route._id,
                routeName: route.routeName,
            }
        });
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    const routes = routesData?.data || [];
    const pagination = routesData?.pagination || {
        totalItems: 0,
        currentPage: 1,
        totalPages: 1
    };

    return (
        <>
            <NavBar />
            <Box p={3} sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5", marginTop: "65px" }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Grid container spacing={3} alignItems="center" justifyContent="flex-end">
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search by route name..."
                                value={searchInput}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchInput && (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClearSearch} size="small">
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <TableContainer component={Paper} elevation={2}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#f8f8f8" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Route Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Origin</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Destination</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                    Stops
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {routes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                        <Typography color="textSecondary">
                                            {debouncedSearch ? "No routes found" : "No routes available"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                routes.map((row, index) => (
                                    <TableRow
                                        key={row._id}
                                        sx={{
                                            "&:hover": { backgroundColor: "#f9f9f9" },
                                            transition: "background-color 0.2s",
                                        }}
                                    >
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{row.routeName}</TableCell>
                                        <TableCell>
                                            {row.origin?.name || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                            {row.destination?.name || "N/A"}
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.stopsCount || 0}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="contained"
                                                sx={{
                                                    backgroundColor: "crimson",
                                                    "&:hover": { backgroundColor: "darkred" },
                                                }}
                                                onClick={() => handleViewSchedule(row)}
                                            >
                                                View Schedule
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={pagination.totalItems}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50]}
                    />
                </TableContainer>
            </Box>
            <LandingFooter />
        </>
    );
};

export default RouteRoutineList;